import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Filter, Star, MapPin, Bike, Car } from 'lucide-react'
import { useLanguageStore, useCurrencyStore } from '../../lib/store'
import { vehiclesApi } from '../../lib/api'
import { formatPrice } from '../../lib/utils'

interface Vehicle {
    id: number
    type: string
    brand: string
    model: string
    dailyRate: number
    location?: { address?: string }
    status: string
    translations?: {
        en?: { title?: string }
        vi?: { title?: string }
    }
    rating?: number
    reviewCount?: number
}

export default function VehiclesPage() {
    const { t } = useTranslation()
    const { language } = useLanguageStore()
    const { currency } = useCurrencyStore()

    const [showFilters, setShowFilters] = useState(false)
    const [vehicles, setVehicles] = useState<Vehicle[]>([])
    const [loading, setLoading] = useState(true)
    const [typeFilter, setTypeFilter] = useState<string>('all')

    useEffect(() => {
        const fetchVehicles = async () => {
            setLoading(true)
            try {
                const params: any = {}
                if (typeFilter !== 'all') {
                    params.type = typeFilter
                }
                const response = await vehiclesApi.list(params)
                if (response.data?.vehicles) {
                    setVehicles(response.data.vehicles)
                }
            } catch (error) {
                console.error('Failed to fetch vehicles:', error)
            } finally {
                setLoading(false)
            }
        }
        fetchVehicles()
    }, [typeFilter])

    const getTitle = (vehicle: Vehicle) => {
        if (vehicle.translations?.en?.title || vehicle.translations?.vi?.title) {
            return language === 'en'
                ? vehicle.translations.en?.title
                : (vehicle.translations.vi?.title || vehicle.translations.en?.title)
        }
        return `${vehicle.brand} ${vehicle.model}`
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-8">
            <div className="container mx-auto px-4">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="text-3xl font-bold mb-2">{t('vehicles')}</h1>
                        <p className="text-gray-600">
                            {language === 'en'
                                ? `${vehicles.length} vehicles available for rent`
                                : `${vehicles.length} xe sẵn sàng cho thuê`}
                        </p>
                    </div>
                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        className="btn btn-secondary md:hidden"
                    >
                        <Filter className="w-5 h-5 mr-2" />
                        {t('filter')}
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                    {/* Filters Sidebar */}
                    <div className={`md:block ${showFilters ? 'block' : 'hidden'}`}>
                        <div className="card sticky top-20">
                            <h3 className="font-bold text-lg mb-4">{t('filter')}</h3>

                            {/* Type Filter */}
                            <div className="mb-6">
                                <label className="block font-semibold mb-3">
                                    {language === 'en' ? 'Vehicle Type' : 'Loại xe'}
                                </label>
                                <div className="space-y-2">
                                    <button
                                        onClick={() => setTypeFilter('all')}
                                        className={`w-full py-2 px-3 rounded-lg text-left flex items-center gap-2 transition ${typeFilter === 'all'
                                            ? 'bg-primary-100 text-primary-700 font-medium'
                                            : 'hover:bg-gray-100'
                                            }`}
                                    >
                                        {language === 'en' ? 'All Types' : 'Tất cả'}
                                    </button>
                                    <button
                                        onClick={() => setTypeFilter('motorcycle')}
                                        className={`w-full py-2 px-3 rounded-lg text-left flex items-center gap-2 transition ${typeFilter === 'motorcycle'
                                            ? 'bg-primary-100 text-primary-700 font-medium'
                                            : 'hover:bg-gray-100'
                                            }`}
                                    >
                                        <Bike className="w-4 h-4" />
                                        {t('motorcycles')}
                                    </button>
                                    <button
                                        onClick={() => setTypeFilter('car')}
                                        className={`w-full py-2 px-3 rounded-lg text-left flex items-center gap-2 transition ${typeFilter === 'car'
                                            ? 'bg-primary-100 text-primary-700 font-medium'
                                            : 'hover:bg-gray-100'
                                            }`}
                                    >
                                        <Car className="w-4 h-4" />
                                        {t('cars')}
                                    </button>
                                </div>
                            </div>

                            {/* Price Range */}
                            <div className="mb-6">
                                <label className="block font-semibold mb-2">
                                    {language === 'en' ? 'Price Range' : 'Khoảng giá'}
                                </label>
                                <div className="flex gap-2">
                                    <input type="number" placeholder="Min" className="input flex-1" />
                                    <input type="number" placeholder="Max" className="input flex-1" />
                                </div>
                            </div>

                            <button className="btn btn-primary w-full">
                                {language === 'en' ? 'Apply Filters' : 'Áp dụng'}
                            </button>
                        </div>
                    </div>

                    {/* Vehicle Grid */}
                    <div className="md:col-span-3">
                        {loading ? (
                            <div className="text-center py-12 text-gray-500">
                                {language === 'en' ? 'Loading vehicles...' : 'Đang tải...'}
                            </div>
                        ) : vehicles.length === 0 ? (
                            <div className="text-center py-12 text-gray-500">
                                {language === 'en' ? 'No vehicles found' : 'Không tìm thấy xe'}
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {vehicles.map((vehicle) => (
                                    <Link
                                        key={vehicle.id}
                                        to={`/vehicles/${vehicle.id}`}
                                        className="card hover:shadow-xl transition-all duration-300 group"
                                    >
                                        <div className="aspect-video bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg mb-4 flex items-center justify-center">
                                            {vehicle.type === 'motorcycle' ? (
                                                <Bike className="w-16 h-16 text-gray-300" />
                                            ) : (
                                                <Car className="w-16 h-16 text-gray-300" />
                                            )}
                                        </div>
                                        <div className="flex items-start justify-between mb-2">
                                            <h3 className="font-bold text-lg group-hover:text-primary-600 transition line-clamp-1 flex-1 mr-2">
                                                {getTitle(vehicle)}
                                            </h3>
                                            <span className={`badge ${vehicle.status === 'active' ? 'badge-success' : 'badge-warning'}`}>
                                                {vehicle.status === 'active'
                                                    ? (language === 'en' ? 'Available' : 'Sẵn sàng')
                                                    : (language === 'en' ? 'Pending' : 'Chờ duyệt')}
                                            </span>
                                        </div>
                                        <p className="text-gray-600 text-sm mb-3 capitalize">
                                            {vehicle.type} • {vehicle.brand}
                                        </p>
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center text-yellow-500">
                                                <Star className="w-4 h-4 fill-current mr-1" />
                                                <span className="font-semibold">
                                                    {vehicle.rating?.toFixed(1) || '5.0'}
                                                </span>
                                                <span className="text-gray-500 text-sm ml-1">
                                                    ({vehicle.reviewCount || 0})
                                                </span>
                                            </div>
                                            <span className="text-sm text-gray-500 flex items-center gap-1">
                                                <MapPin className="w-3 h-3" />
                                                {vehicle.location?.address || 'Dương Đông'}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between pt-3 border-t gap-2">
                                            <span className="text-lg md:text-xl font-bold text-primary-600 whitespace-nowrap">
                                                {formatPrice(vehicle.dailyRate || 0, currency)}
                                                <span className="text-xs md:text-sm font-normal text-gray-500">
                                                    /{language === 'en' ? 'day' : 'ngày'}
                                                </span>
                                            </span>
                                            <span className="btn btn-primary text-sm whitespace-nowrap">
                                                {t('book_now')}
                                            </span>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}
